import { useEffect, useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Plus } from 'lucide-react';
import api from '@/lib/api';

export default function SupplierAutocomplete({ value, onChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [items, setItems] = useState([]);

  useEffect(() => setQuery(value || ''), [value]);

  const load = useCallback(async (q) => {
    try {
      const r = await api.get('/suppliers', { params: { search: q || undefined } });
      setItems(r.data);
    } catch (e) {}
  }, []);

  useEffect(() => { if (open) load(query); }, [open, query, load]);

  const exact = items.find((s) => s.name.toLowerCase() === (query || '').toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            data-testid="pengeluaran-supplier-autocomplete"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); if (!open) setOpen(true); }}
            placeholder="Ketik nama supplier..."
            className="pr-8"
          />
          <ChevronsUpDown className="w-3.5 h-3.5 text-[var(--fpccs-text-subtle)] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false} className="bg-transparent">
          <CommandList>
            {items.length === 0 && query && (
              <CommandEmpty className="py-2 px-3 text-xs">Tidak ditemukan.</CommandEmpty>
            )}
            {items.length > 0 && (
              <CommandGroup heading="Master Supplier">
                {items.slice(0, 30).map((s) => (
                  <CommandItem
                    key={s.id}
                    value={s.name}
                    onSelect={() => { onChange?.(s.name); onSelect?.(s); setQuery(s.name); setOpen(false); }}
                    data-testid={`supplier-option-${s.id}`}
                  >
                    <div className="flex flex-col">
                      <span>{s.name}</span>
                      {(s.pic || s.phone) && <span className="text-[10px] text-subtle-fpccs">{s.pic} {s.phone && `• ${s.phone}`}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {query && !exact && (
              <CommandGroup heading="Baru">
                <CommandItem
                  value={`new-${query}`}
                  onSelect={() => { onChange?.(query); onSelect?.({ __isNew: true, name: query }); setOpen(false); }}
                  className="text-[var(--fpccs-primary)]"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" /> Gunakan "{query}"
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
